import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { CloseIcon, SmallAddIcon } from '@chakra-ui/icons';

const VAT_RATE = 0.2;
const AMOUNT_REGEXP = /^\d+(\.\d{1,2})?$/; // positive number with up to 2 decimals

function toNumber(value) {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value);
  return NaN;
}

function calcLine(amount, vat) {
  const net = parseFloat((toNumber(amount) || 0).toFixed(2));
  const vatAmount = vat ? parseFloat((net * VAT_RATE).toFixed(2)) : 0;
  const grossAmount = parseFloat((net + vatAmount).toFixed(2));

  return { net, vatAmount, grossAmount };
}

export function getInvoiceItemsTotals(items) {
  let netAmount = 0;
  let vatAmount = 0;

  (items || []).forEach(({ amount, vat }) => {
    const line = calcLine(amount, !!vat);
    netAmount += line.net;
    vatAmount += line.vatAmount;
  });

  const grossAmount = parseFloat((netAmount + vatAmount).toFixed(2));

  return {
    netAmount: parseFloat(netAmount.toFixed(2)),
    vatAmount: parseFloat(vatAmount.toFixed(2)),
    grossAmount,
  };
}

function createErrorsFields(items) {
  return (items || []).map(() => ({ title: '', amount: '' }));
}

export default function InvoiceItemsInputs({
  items,
  currencySymbol,
  onItemsChange,
  onHasErrorsChange,
}) {
  const [errors, setErrors] = useState(() => createErrorsFields(items));

  useEffect(() => {
    if ((items || []).length !== errors.length) {
      setErrors(createErrorsFields(items));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items?.length]);

  const hasErrors = useMemo(() => {
    if (!items || items.length === 0) return true;
    const hasEmptyRequired = items.some(
      (item) => (item?.title || '') === '' || (item?.amount ?? '') === ''
    );
    return hasEmptyRequired || errors.some((e) => e?.title || e?.amount);
  }, [errors, items]);

  useEffect(() => {
    if (typeof onHasErrorsChange === 'function') {
      onHasErrorsChange(hasErrors);
    }
  }, [hasErrors, onHasErrorsChange]);

  const validate = (fieldValues, index) => {
    const list = [...errors];
    if (!(index in list)) list[index] = {};

    if ('title' in fieldValues) {
      if (fieldValues.title === '') {
        list[index].title = 'This is mandatory field';
      } else if (fieldValues.title.length > 255) {
        list[index].title = 'Please enter 250 characters or less';
      } else {
        list[index].title = '';
      }
    }

    if ('amount' in fieldValues) {
      if (fieldValues.amount === '') {
        list[index].amount = 'This is mandatory field';
      } else if (!AMOUNT_REGEXP.test(fieldValues.amount)) {
        list[index].amount =
          'Only numerical input is allowed, if decimal number is needed please use . to separate numbers (e.g. 3.14).';
      } else if (fieldValues.amount === '0') {
        list[index].amount = 'Only input larger than 0 is allowed.';
      } else {
        list[index].amount = '';
      }
    }

    setErrors(list);
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const next = [...items];
    next[index] = {
      ...next[index],
      [name]: value,
    };
    onItemsChange(next);
    validate({ [name]: value }, index);
  };

  const handleVatChange = (e, index) => {
    const next = [...items];
    next[index] = {
      ...next[index],
      vat: e.target.checked,
    };
    onItemsChange(next);
  };

  const handleRemoveClick = (index) => {
    const next = [...items];
    next.splice(index, 1);
    const errNext = [...errors];
    errNext.splice(index, 1);
    setErrors(errNext);
    onItemsChange(next);
  };

  const handleAddClick = () => {
    const next = [
      ...items,
      {
        id: null,
        clientId: `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        title: '',
        amount: '',
        vat: false,
      },
    ];
    onItemsChange(next);
    setErrors([...errors, { title: '', amount: '' }]);
  };

  const { netAmount, vatAmount, grossAmount } = getInvoiceItemsTotals(items);

  return (
    <Box>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th minWidth="500px" pl="0" pb="0" pr="0">
              <Box color="#bdc1d0" fontSize="12px" fontWeight="700">
                item title
              </Box>
            </Th>
            <Th pl="0" pb="0">
              <Box color="#bdc1d0" pb="0" fontSize="12px" fontWeight="700">
                amount(net)
              </Box>
            </Th>
            <Th pb="0" pl="0">
              <Box
                color="#bdc1d0"
                pb="0"
                pl="0"
                fontSize="12px"
                fontWeight="700"
                lineHeight="1"
                textAlign="left"
              >
                vat
              </Box>
            </Th>
            <Th pl="10px" pb="0" pr="0">
              <Box
                color="#bdc1d0"
                pb="0"
                fontSize="12px"
                fontWeight="700"
                textAlign="left"
              >
                amount(gross)
              </Box>
            </Th>
            <Th pl="10px" pb="0" pr="0">
              <Box
                color="#bdc1d0"
                pb="0"
                fontSize="12px"
                fontWeight="700"
                textAlign="right"
              >
                Add VAT
              </Box>
            </Th>
            <Th pl="10px" pb="0" pr="0" />
          </Tr>
        </Thead>
        <Tbody>
          {(items || []).map((item, index) => {
            const key = item.clientId || item.id || index;
            const title = item.title || '';
            const amount = item.amount ?? '';
            const vat = !!item.vat;
            const line = calcLine(amount, vat);

            return (
              <Tr key={key}>
                <Td pl="0" p="10px">
                  <FormControl>
                    <Input
                      name="title"
                      focusBorderColor="black"
                      errorBorderColor="error.500"
                      placeholder="Enter item title"
                      value={title}
                      onChange={(e) => handleInputChange(e, index)}
                      isInvalid={!!errors[index]?.title}
                      bg="#ebecf0"
                      border="1px solid transparent"
                      _focus={{ border: '1px solid black' }}
                      _hover={{ border: '1px solid black' }}
                      size="sm"
                    />
                    {errors[index]?.title ? (
                      <Box color="error.500" fontSize="13px" mt="6px">
                        {errors[index].title}
                      </Box>
                    ) : null}
                  </FormControl>
                </Td>
                <Td p="10px" pl="0">
                  <FormControl>
                    <Input
                      name="amount"
                      focusBorderColor="black"
                      errorBorderColor="error.500"
                      placeholder="Enter net amount"
                      value={amount}
                      onChange={(e) => handleInputChange(e, index)}
                      isInvalid={!!errors[index]?.amount}
                      bg="#ebecf0"
                      border="1px solid transparent"
                      _focus={{ border: '1px solid black' }}
                      _hover={{ border: '1px solid black' }}
                      size="sm"
                    />
                    {errors[index]?.amount ? (
                      <Box color="error.500" fontSize="13px" mt="6px">
                        {errors[index].amount}
                      </Box>
                    ) : null}
                  </FormControl>
                </Td>
                <Td isNumeric p="10px" pl="0">
                  <Box>
                    {currencySymbol}
                    {line.vatAmount.toFixed(2)}
                  </Box>
                </Td>
                <Td isNumeric p="10px">
                  <Box>
                    {currencySymbol}
                    {line.grossAmount.toFixed(2)}
                  </Box>
                </Td>
                <Td isNumeric p="10px">
                  <Box textAlign="right">
                    <Checkbox
                      colorScheme="brand"
                      _focus={{ border: '1px solid black' }}
                      isChecked={vat}
                      onChange={(e) => handleVatChange(e, index)}
                    >
                      <Text fontSize="16px" lineHeight="1">
                        Add VAT
                      </Text>
                    </Checkbox>
                  </Box>
                </Td>
                <Td isNumeric p="10px">
                  {(items || []).length > 1 ? (
                    <IconButton
                      colorScheme="brand"
                      size="xs"
                      onClick={() => handleRemoveClick(index)}
                      aria-label="remove-invoice-item"
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : null}
                </Td>
              </Tr>
            );
          })}

          <Tr>
            <Td pl="0" p="10px" />
            <Td pl="0" p="10px" />
            <Td pl="0" p="10px" />
            <Td p="10px" isNumeric>
              <Box color="#868789">Sub-total (net) </Box>
              <Box color="#868789">VAT sub-total</Box>
              <Box color="#868789" mt="20px">
                TOTAL (gross){' '}
              </Box>
            </Td>

            <Td isNumeric p="10px">
              <Box textAlign="right">
                {currencySymbol}
                {netAmount.toFixed(2)}
              </Box>
              <Box textAlign="right">
                {currencySymbol}
                {vatAmount.toFixed(2)}
              </Box>
              <Box fontSize="20px" fontWeight="bold" textAlign="right" mt="20px">
                {currencySymbol}
                {grossAmount.toFixed(2)}
              </Box>
            </Td>
            <Td />
          </Tr>
        </Tbody>
      </Table>

      <Grid templateColumns="1fr" mt={4}>
        <Box>
          <Button
            colorScheme="brand"
            variant="ghost"
            size="xs"
            p="0px"
            onClick={handleAddClick}
            fontSize="14px"
          >
            <SmallAddIcon h={22} w={7} />
            Add item
          </Button>
        </Box>
      </Grid>
    </Box>
  );
}
